import React from 'react';
import { expandLiteralValues } from '../../../utils/literalValues';

const HEADING_3 = 'revy-cjk mt-2 mb-1 text-[0.8rem] font-bold leading-[1.5]';
const HEADING_2 = 'revy-cjk mt-3 mb-1 text-[0.85rem] font-bold leading-[1.5]';
const LINE = 'revy-cjk text-[0.78rem] leading-[1.5]';
const LIST_LINE = 'revy-cjk pl-3 text-[0.78rem] leading-[1.5]';
const QUOTE =
  'my-1 rounded-r border-l-2 border-warning bg-accent/60 pl-2';
const INLINE_CODE = 'rounded-sm bg-foreground/10 px-[3px] py-px text-[0.72rem]';

function formatInline(text: string, lit: (text: string) => string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{lit(boldMatch[1])}</span>);
      parts.push(<strong key={key++}>{lit(boldMatch[2])}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{lit(codeMatch[1])}</span>);
      parts.push(
        <code key={key++} className={INLINE_CODE}>
          {lit(codeMatch[2])}
        </code>
      );
      remaining = codeMatch[3];
      continue;
    }

    parts.push(<span key={key++}>{lit(remaining)}</span>);
    break;
  }

  return <>{parts}</>;
}

/**
 * Simple Markdown-to-JSX renderer for chat messages.
 * Handles headers, lists, bold, code, and blockquotes.
 */
const ChatMarkdown: React.FC<{ text: string; literalValues?: string[] }> = ({ text, literalValues }) => {
  const lit = (value: string) => expandLiteralValues(value, literalValues);
  const elements: React.ReactNode[] = [];

  text.split('\n').forEach((line, i) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('### ')) {
      elements.push(
        <p key={i} className={HEADING_3}>
          {lit(trimmed.slice(4))}
        </p>
      );
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <p key={i} className={HEADING_2}>
          {lit(trimmed.slice(3))}
        </p>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <p key={i} className={LIST_LINE}>
          {'• '}{formatInline(trimmed.slice(2), lit)}
        </p>
      );
    } else if (/^\d+\.\s/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <p key={i} className={LIST_LINE}>
            {match[1]}. {formatInline(match[2], lit)}
          </p>
        );
      }
    } else if (trimmed.startsWith('> ')) {
      elements.push(
        <div key={i} className={QUOTE}>
          <span className="revy-cjk block text-xs leading-[1.5]">{lit(trimmed.slice(2))}</span>
        </div>
      );
    } else if (trimmed === '') {
      elements.push(<div key={i} className="h-[3px]" />);
    } else {
      elements.push(
        <p key={i} className={LINE}>
          {formatInline(trimmed, lit)}
        </p>
      );
    }
  });

  return <>{elements}</>;
};

export default ChatMarkdown;
